"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * Modal component - dialog/modal overlay
 */
const react_1 = require("react");
const react_dom_1 = require("react-dom");
require("./Modal.scss");
/**
 * Modal component for displaying dialogs and overlays
 * @param props - ModalProps
 * @returns Modal component rendered in a portal
 */
const Modal = ({ isOpen, onClose, title, children, footer, closeOnBackdrop = true, closeOnEscape = true, size = 'medium', className = '', }) => {
    const modalRef = (0, react_1.useRef)(null);
    // Handle escape key and focus trap
    (0, react_1.useEffect)(() => {
        if (!isOpen)
            return;
        const handleKeyDown = (event) => {
            // Handle Escape key
            if (closeOnEscape && event.key === 'Escape') {
                onClose();
                return;
            }
            // Handle Tab key for focus trap
            if (event.key === 'Tab' && modalRef.current) {
                const focusableElements = modalRef.current.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
                const focusableArray = Array.from(focusableElements);
                if (focusableArray.length === 0) {
                    // If no focusable elements, prevent tabbing
                    event.preventDefault();
                    return;
                }
                const firstElement = focusableArray[0];
                const lastElement = focusableArray[focusableArray.length - 1];
                if (event.shiftKey) {
                    // Shift+Tab: if on first element, wrap to last
                    if (document.activeElement === firstElement) {
                        event.preventDefault();
                        lastElement.focus();
                    }
                }
                else {
                    // Tab: if on last element, wrap to first
                    if (document.activeElement === lastElement) {
                        event.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeOnEscape, onClose]);
    // Focus management - focus modal on open and restore focus on close
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (isOpen) {
            const previousActiveElement = document.activeElement;
            // Focus the first focusable element or the modal container
            const focusableElements = (_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
            if (focusableElements && focusableElements.length > 0) {
                focusableElements[0].focus();
            }
            else {
                (_b = modalRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
            return () => {
                previousActiveElement === null || previousActiveElement === void 0 ? void 0 : previousActiveElement.focus();
            };
        }
    }, [isOpen]);
    // Body scroll lock - prevent background scrolling when modal is open
    (0, react_1.useEffect)(() => {
        if (isOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen]);
    if (!isOpen)
        return null;
    const handleBackdropClick = (event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) {
            onClose();
        }
    };
    const modalClasses = ['dp-modal', `dp-modal--${size}`, className].filter(Boolean).join(' ');
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)("div", { className: "dp-modal-backdrop", onClick: handleBackdropClick, onKeyDown: (e) => e.key === 'Escape' && onClose(), role: "presentation", children: (0, jsx_runtime_1.jsxs)("div", { ref: modalRef, className: modalClasses, role: "dialog", "aria-modal": "true", "aria-labelledby": title ? 'modal-title' : undefined, tabIndex: -1, children: [title && ((0, jsx_runtime_1.jsxs)("div", { className: "dp-modal__header", children: [(0, jsx_runtime_1.jsx)("h2", { id: "modal-title", className: "dp-modal__title", children: title }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "dp-modal__close", onClick: onClose, "aria-label": "Close modal", children: "\u00D7" })] })), (0, jsx_runtime_1.jsx)("div", { className: "dp-modal__content", children: children }), footer && (0, jsx_runtime_1.jsx)("div", { className: "dp-modal__footer", children: footer })] }) }), document.body);
};
exports.Modal = Modal;
exports.default = exports.Modal;
